'use client';

import Link from 'next/link';
import NextImage from 'next/image';
import { useState } from 'react';
import siteTheme from '@/lib/theme';
import type { SiteSettings, CmsFooter, CmsPartnerLogo, CmsSocialLink } from '@/lib/cms';
import { parseMarkdown } from '@/lib/markdown';

const { colors, fonts } = siteTheme;

const pad = 'clamp(2rem, 6vw, 8rem)';

const underlineInput: React.CSSProperties = {
  flex: 1,
  fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
  color: colors.gold, backgroundColor: 'transparent',
  border: 'none', borderBottom: `1px solid ${colors.goldPale}`,
  padding: '1rem 0', outline: 'none', minWidth: 0,
};

// ── Social icon SVGs keyed by platform name ─────────────────────────────────
const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  facebook: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
    </svg>
  ),
  instagram: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  ),
  youtube: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
    </svg>
  ),
  whatsapp: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
  tripadvisor: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z" />
    </svg>
  ),
  twitter: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  linkedin: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" />
    </svg>
  ),
};


export default function Footer({ settings, footer }: { settings?: SiteSettings | null; footer?: CmsFooter | null }) {
  const siteName = settings?.site_name ?? siteTheme.name;
  const siteTagline = settings?.tagline ?? siteTheme.tagline;

  // Newsletter — from Footer CMS
  const showNewsletter = footer?.newsletter?.enabled ?? true;
  const newsletterTitle = footer?.newsletter?.title ?? 'Newsletter';
  const newsletterSub = footer?.newsletter?.description ?? 'Stay up to date with\nour latest news';
  const newsletterBtn = footer?.newsletter?.button_text ?? 'Subscribe';

  // Social links — from Footer CMS col_social
  const socialLinks: CmsSocialLink[] = footer?.col_social?.social_links ?? [];
  const footerLinks = footer?.col_social?.nav_links ?? [];

  // Brand column
  const footerLogo = footer?.col_brand?.logo ?? null;
  const footerDesc = footer?.col_brand?.show_description ? (footer?.col_brand?.description ?? siteTagline) : null;

  // Contact column
  const siteAddress = footer?.col_contact?.address ?? null;
  const phones = footer?.col_contact?.phones ?? [];
  const emails = footer?.col_contact?.emails ?? [];

  // Certificates
  const partnerLogos: CmsPartnerLogo[] = footer?.certificates?.logos ?? [];

  // Bottom bar
  const copyrightText = footer?.bottom_bar?.copyright_text ?? null;
  const legalLinks = footer?.bottom_bar?.links?.length
    ? footer.bottom_bar.links
    : [];

  const showSocial = true;
  const showLinks = true;
  const showLegalBar = true;

  const [nameVal, setNameVal] = useState('');
  const [emailVal, setEmailVal] = useState('');
  const [agreed, setAgreed] = useState(false);

  return (
    <footer>

      {/* ══════════════════════════════════════════════════════
          NEWSLETTER
      ══════════════════════════════════════════════════════ */}
      {showNewsletter && <div style={{ backgroundColor: colors.bg, borderTop: `1px solid ${colors.goldPale}` }}>
        <div style={{
          maxWidth: '144rem', margin: '0 auto',
          padding: `clamp(4rem, 6vw, 7rem) ${pad}`,
        }}>

          {/* ── DESKTOP: 2-col (title left | form right) ── */}
          <div className="hidden lg:grid" style={{
            gridTemplateColumns: '1fr 2fr',
            gap: 'clamp(4rem, 6vw, 10rem)',
            alignItems: 'flex-start',
          }}>
            {/* LEFT */}
            <div>
              <p style={{
                fontFamily: fonts.script, fontWeight: 400,
                fontSize: 'clamp(3rem, 3.5vw, 4.5rem)', color: colors.gold,
                lineHeight: 1, marginBottom: '1rem',
              }}>{newsletterTitle}</p>
              <p style={{
                fontFamily: fonts.sans, fontWeight: 400,
                fontSize: '1.4rem', letterSpacing: '0.18em', textTransform: 'uppercase',
                color: colors.goldLight, lineHeight: 1.5,
              }}>
                {newsletterSub.split('\n').map((line, i) => (
                  <span key={i}>{line}{i < newsletterSub.split('\n').length - 1 && <br />}</span>
                ))}
              </p>
            </div>

            {/* RIGHT: inputs row + legal below */}
            <div>
              <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-end', marginBottom: '1.6rem' }}>
                <input type="text" placeholder="Name" value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)} style={underlineInput} />
                <input type="email" placeholder="Email" value={emailVal}
                  onChange={(e) => setEmailVal(e.target.value)} style={{ ...underlineInput, flex: 1.5 }} />
                <button type="button" className="btn-luxury-nav" style={{
                  fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: '#fff', backgroundColor: colors.gold,
                  border: '1px solid transparent', padding: '1rem 2.8rem',
                  borderRadius: '0.5rem',
                  cursor: 'pointer', flexShrink: 0,
                }}>Send</button>
              </div>
              {/* Legal */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div onClick={() => setAgreed(!agreed)} style={{
                  width: '1.3rem', height: '1.3rem', flexShrink: 0,
                  border: `1px solid ${colors.goldPale}`,
                  backgroundColor: agreed ? colors.gold : 'transparent',
                  marginTop: '0.2rem', cursor: 'pointer',
                }} />
                <p style={{
                  fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
                  color: colors.goldLight, lineHeight: 1.6,
                }}>
                  Legal notice: The personal data provided in this form will be processed by {siteName}
                  for the purpose of handling your subscription request. For further information about our{' '}
                  <Link href="/privacy-policy" style={{ textDecoration: 'underline', color: colors.goldLight }}>privacy policy</Link>,
                  click HERE.
                </p>
              </div>
            </div>
          </div>

          {/* ── MOBILE: stacked ── */}
          <div className="lg:hidden">
            <p style={{
              fontFamily: fonts.script, fontWeight: 400,
              fontSize: 'clamp(3.2rem, 8vw, 4.5rem)', color: colors.gold,
              lineHeight: 1, marginBottom: '1rem',
            }}>{newsletterTitle}</p>
            <p style={{
              fontFamily: fonts.sans, fontWeight: 400,
              fontSize: 'clamp(1.4rem, 3.5vw, 1.6rem)', letterSpacing: '0.18em',
              textTransform: 'uppercase', color: colors.goldLight, lineHeight: 1.5,
              marginBottom: 'clamp(3rem, 6vw, 4rem)',
            }}>
              {newsletterSub.split('\n').map((line, i) => (
                <span key={i}>{line}{i < newsletterSub.split('\n').length - 1 && <br />}</span>
              ))}
            </p>
            {/* Name */}
            <div style={{ marginBottom: '2rem' }}>
              <input type="text" placeholder="Name" value={nameVal}
                onChange={(e) => setNameVal(e.target.value)}
                style={{ ...underlineInput, display: 'block', width: '100%' }} />
            </div>
            {/* Email */}
            <div style={{ marginBottom: '2.4rem' }}>
              <input type="email" placeholder="Email" value={emailVal}
                onChange={(e) => setEmailVal(e.target.value)}
                style={{ ...underlineInput, display: 'block', width: '100%' }} />
            </div>
            {/* Legal */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '3rem' }}>
              <div onClick={() => setAgreed(!agreed)} style={{
                width: '1.5rem', height: '1.5rem', flexShrink: 0,
                border: `1px solid ${colors.goldPale}`,
                backgroundColor: agreed ? colors.gold : 'transparent',
                marginTop: '0.2rem', cursor: 'pointer',
              }} />
              <p style={{
                fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
                color: colors.goldLight, lineHeight: 1.6,
              }}>
                Legal notice: The personal data provided in this form will be processed by {siteName}
                for the purpose of handling your subscription request. For further information about our{' '}
                <Link href="/privacy-policy" style={{ textDecoration: 'underline', color: colors.goldLight }}>privacy policy</Link>,
                click HERE.
              </p>
            </div>
            {/* Send */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button type="button" className="btn-luxury-nav" style={{
                fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                color: '#fff', backgroundColor: colors.gold,
                border: '1px solid transparent', padding: '1.2rem 5rem',
                borderRadius: '0.5rem',
                cursor: 'pointer', width: '100%', maxWidth: '26rem',
              }}>Send</button>
            </div>
          </div>

        </div>
      </div>}

      {/* ══════════════════════════════════════════════════════
          CREST SECTION
      ══════════════════════════════════════════════════════ */}
      <div style={{ backgroundColor: colors.bg, borderTop: `1px solid ${colors.goldPale}` }}>
        <div style={{ maxWidth: '144rem', margin: '0 auto', padding: `clamp(3.5rem, 3.5vw, 3.5rem) ${pad}` }}>

          {/* ── DESKTOP: 3-col grid ── */}
          <div className="hidden lg:flex" style={{
            //gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            //gap: 'clamp(3rem, 5vw, 7rem)',
            padding: 'clamp(4rem, 6vw, 7rem) 0',
            justifyContent: 'space-between'

          }}>

            {/* LEFT: social icons top, nav links bottom */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', gap: '3rem' }}>
              {/* Social icons */}
              {showSocial && (
                <div style={{ display: 'flex', gap: '2rem' }}>
                  {socialLinks.map((s) => (
                    <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                      aria-label={s.label || s.platform}
                      className="footer-social"
                      style={{ color: colors.gold, opacity: 0.85 }}>
                      {SOCIAL_ICONS[s.platform.toLowerCase()] ?? SOCIAL_ICONS.facebook}
                    </a>
                  ))}
                </div>
              )}
              {/* Nav links */}
              {showLinks && (
                <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap' }}>
                  {footerLinks.map((link) => (
                    <Link key={link.label} href={link.href} className="footer-link" style={{
                      fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
                      letterSpacing: '0.22em', textTransform: 'uppercase',
                      color: colors.goldLight, textDecoration: 'none',
                    }}>{link.label}</Link>
                  ))}
                </div>
              )}
            </div>

            {/* CENTER: Crest — larger, more prominent */}
            <Link href="/" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
              {footerLogo ? (
                <>
                  <div style={{ position: 'relative', height: '9rem', width: '12rem', marginBottom: '0.8rem' }}>
                    <NextImage src={footerLogo.url} alt={footerLogo.alternativeText || siteName} fill sizes="192px" style={{ objectFit: 'contain' }} />
                  </div>
                  {/* Description below logo */}
                  {footerDesc && (
                    <p style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.goldLight, lineHeight: 1.6, marginTop: '0.3rem', textAlign: 'center', maxWidth: '24rem' }}>{footerDesc}</p>
                  )}
                </>
              ) : (
                <>
                  {/* Crown */}
                  <svg width="72" height="36" viewBox="0 0 60 28" fill="none" style={{ marginBottom: '0.8rem' }}>
                    <path d="M5 28 L5 14 L18 4 L30 14 L42 4 L55 14 L55 28" stroke={colors.gold} strokeWidth="1" fill="none" />
                    <circle cx="5" cy="14" r="2.5" fill={colors.gold} />
                    <circle cx="30" cy="4" r="2.5" fill={colors.gold} />
                    <circle cx="55" cy="14" r="2.5" fill={colors.gold} />
                  </svg>
                  {/* Divider */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.4rem' }}>
                    <span style={{ width: '4rem', height: '1px', backgroundColor: colors.goldPale }} />
                    <svg width="6" height="6" viewBox="0 0 8 8" fill="none"><path d="M4 0L4.93 3.07L8 4L4.93 4.93L4 8L3.07 4.93L0 4L3.07 3.07L4 0Z" fill={colors.goldPale} /></svg>
                    <span style={{ width: '4rem', height: '1px', backgroundColor: colors.goldPale }} />
                  </div>
                  {/* Monogram */}
                  <p style={{ fontFamily: fonts.serif, fontSize: '7.5rem', fontWeight: 300, letterSpacing: '0.08em', lineHeight: 1, color: colors.gold }}>SR</p>
                  {/* Stars */}
                  <div style={{ display: 'flex', gap: '0.5rem', margin: '0.6rem 0' }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <svg key={s} width="10" height="10" viewBox="0 0 12 12" fill={colors.gold} opacity="0.6">
                        <path d="M6 0l1.4 4.1H12L8.6 6.7l1.4 4.1L6 8.2l-4 2.6 1.4-4.1L0 4.1h4.6z" />
                      </svg>
                    ))}
                  </div>
                  {/* Script name */}
                  <p style={{ fontFamily: fonts.script, fontSize: '3.2rem', fontWeight: 400, lineHeight: 1, color: colors.gold }}>{siteName}</p>
                  {/* Tagline */}
                  <p style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: colors.goldLight, lineHeight: 1, marginTop: '0.3rem' }}>{siteTagline}</p>
                  {/* Bottom divider */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.8rem' }}>
                    <span style={{ width: '4rem', height: '1px', backgroundColor: colors.goldPale }} />
                    <svg width="6" height="6" viewBox="0 0 8 8" fill="none"><path d="M4 0L4.93 3.07L8 4L4.93 4.93L4 8L3.07 4.93L0 4L3.07 3.07L4 0Z" fill={colors.goldPale} /></svg>
                    <span style={{ width: '4rem', height: '1px', backgroundColor: colors.goldPale }} />
                  </div>
                </>
              )}
            </Link>

            {/* RIGHT: contact info — Wellington style */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.8rem', alignSelf: 'center' }}>
              {/* Address block */}
              {(siteName || siteAddress) && (
                <div style={{
                  fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
                  lineHeight: 1.6, letterSpacing: '0.04em', textTransform: 'none',
                  color: colors.goldLight,
                }}>
                  {siteName && <><span style={{ color: colors.gold, fontWeight: 400 }}>{siteName}</span><br /></>}

                  {siteAddress && (<span
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(siteAddress) }}
                  />)}
                </div>
              )}
              {/* Phones */}
              {phones.length > 0 && (
                <div style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, lineHeight: 1.6, letterSpacing: '0.04em', color: colors.goldLight }}>
                  {phones.map((p) => (
                    <a key={p.id} href={`tel:${p.number.replace(/\\s+/g, '')}`} style={{ display: 'block', color: colors.goldLight, textDecoration: 'none' }}>{p.number}</a>
                  ))}
                </div>
              )}
              {/* Emails */}
              {emails.length > 0 && (
                <div style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, lineHeight: 1.6, letterSpacing: '0.04em', color: colors.goldLight }}>
                  {emails.map((e) => (
                    <a key={e.id} href={`mailto:${e.email}`} style={{ display: 'block', color: colors.goldLight, textDecoration: 'none' }}>{e.email}</a>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* ── MOBILE: centered stack ── */}
          <div className="flex lg:hidden" style={{ flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '3rem' }}>
            {/* Crest / Logo */}
            <Link href="/" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', color: colors.gold, textDecoration: 'none' }}>
              {footerLogo ? (
                <>
                  <div style={{ position: 'relative', height: '10rem', width: '16rem' }}>
                    <NextImage src={footerLogo.url} alt={footerLogo.alternativeText || siteName} fill sizes="256px" style={{ objectFit: 'contain' }} />
                  </div>
                  {footerDesc && (
                    <p style={{ fontFamily: fonts.sans, fontSize: '1.3rem', fontWeight: 400, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.goldLight, lineHeight: 1.6, marginTop: '0.4rem', maxWidth: '22rem' }}>{footerDesc}</p>
                  )}
                </>
              ) : (
                <>
                  <svg width="48" height="24" viewBox="0 0 60 28" fill="none" style={{ marginBottom: '0.6rem' }}>
                    <path d="M5 28 L5 14 L18 4 L30 14 L42 4 L55 14 L55 28" stroke={colors.goldPale} strokeWidth="1" fill="none" />
                    <circle cx="5" cy="14" r="2" fill={colors.goldPale} />
                    <circle cx="30" cy="4" r="2" fill={colors.goldPale} />
                    <circle cx="55" cy="14" r="2" fill={colors.goldPale} />
                  </svg>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span style={{ width: '3rem', height: '1px', backgroundColor: colors.goldPale }} />
                    <svg width="7" height="7" viewBox="0 0 8 8" fill="none"><path d="M4 0L4.93 3.07L8 4L4.93 4.93L4 8L3.07 4.93L0 4L3.07 3.07L4 0Z" fill={colors.goldPale} /></svg>
                    <span style={{ width: '3rem', height: '1px', backgroundColor: colors.goldPale }} />
                  </div>
                  <p style={{ fontFamily: fonts.serif, fontSize: '5rem', fontWeight: 300, letterSpacing: '0.1em', lineHeight: 1, color: colors.gold, marginTop: '0.3rem' }}>SR</p>
                  <div style={{ display: 'flex', gap: '0.4rem', margin: '0.4rem 0' }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <svg key={s} width="9" height="9" viewBox="0 0 12 12" fill={colors.goldPale}>
                        <path d="M6 0l1.4 4.1H12L8.6 6.7l1.4 4.1L6 8.2l-4 2.6 1.4-4.1L0 4.1h4.6z" />
                      </svg>
                    ))}
                  </div>
                  <p style={{ fontFamily: fonts.script, fontSize: '2.4rem', fontWeight: 400, lineHeight: 1, color: colors.gold }}>{siteName}</p>
                  <p style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400, letterSpacing: '0.35em', textTransform: 'uppercase', color: colors.goldLight, lineHeight: 1, marginTop: '0.2rem' }}>{siteTagline}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginTop: '0.4rem' }}>
                    <span style={{ width: '3rem', height: '1px', backgroundColor: colors.goldPale }} />
                    <svg width="7" height="7" viewBox="0 0 8 8" fill="none"><path d="M4 0L4.93 3.07L8 4L4.93 4.93L4 8L3.07 4.93L0 4L3.07 3.07L4 0Z" fill={colors.goldPale} /></svg>
                    <span style={{ width: '3rem', height: '1px', backgroundColor: colors.goldPale }} />
                  </div>
                </>
              )}
            </Link>
            {/* Contact info — Wellington style */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.6rem', textAlign: 'center' }}>
              {(siteName || siteAddress) && (
                <div style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, lineHeight: 1.6, letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.goldLight }}>
                  {siteName && <><span style={{ color: colors.gold, fontWeight: 400 }}>{siteName}</span><br /></>}
                  {siteAddress && (<span
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(siteAddress) }}
                  />)}
                </div>
              )}
              {phones.length > 0 && (
                <div style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, lineHeight: 1.6, letterSpacing: '0.04em', color: colors.goldLight }}>
                  {phones.map((p) => (
                    <a key={p.id} href={`tel:${p.number.replace(/\\s+/g, '')}`} style={{ display: 'block', color: colors.goldLight, textDecoration: 'none' }}>{p.number}</a>
                  ))}
                </div>
              )}
              {emails.length > 0 && (
                <div style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, lineHeight: 1.6, letterSpacing: '0.04em', color: colors.goldLight }}>
                  {emails.map((e) => (
                    <a key={e.id} href={`mailto:${e.email}`} style={{ display: 'block', color: colors.goldLight, textDecoration: 'none' }}>{e.email}</a>
                  ))}
                </div>
              )}
            </div>
            {/* Nav links */}
            {showLinks && (
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '2rem 3rem' }}>
                {footerLinks.map((link) => (
                  <Link key={link.label} href={link.href} className="footer-link" style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400, letterSpacing: '0.18em', textTransform: 'uppercase', color: colors.goldLight, textDecoration: 'none' }}>{link.label}</Link>
                ))}
              </div>
            )}
            {/* Social icons */}
            {showSocial && (
              <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center' }}>
                {socialLinks.map((s) => (
                  <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                    aria-label={s.label || s.platform}
                    className="footer-social"
                    style={{ color: colors.gold }}>
                    {SOCIAL_ICONS[s.platform.toLowerCase()] ?? SOCIAL_ICONS.facebook}
                  </a>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          PARTNER LOGOS STRIP
      ══════════════════════════════════════════════════════ */}
      {partnerLogos.length > 0 && (
        <div style={{ backgroundColor: colors.bgAlt, borderTop: `1px solid ${colors.goldPale}`, borderBottom: `1px solid ${colors.goldPale}` }}>
          <div style={{
            maxWidth: '144rem', margin: '0 auto',
            padding: `clamp(2.4rem, 3vw, 3.6rem) ${pad}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexWrap: 'wrap', gap: 'clamp(3rem, 5vw, 7rem)',
          }}>
            {partnerLogos.map((partner) => {
              const img = (
                <div style={{ position: 'relative', height: '4rem', width: 'auto', minWidth: '6rem', maxWidth: '14rem' }}>
                  <NextImage
                    src={partner.logo.url}
                    alt={partner.name}
                    fill
                    sizes="140px"

                    style={{ objectFit: 'contain', filter: 'grayscale(1) opacity(0.55)', transition: 'filter 0.3s ease' }}
                    onMouseEnter={(e) => (e.currentTarget.style.filter = 'grayscale(0) opacity(1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.filter = 'grayscale(1) opacity(0.55)')}
                  />
                </div>
              );
              return partner.url ? (
                <a key={partner.id} href={partner.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                  {img}
                </a>
              ) : (
                <div key={partner.id} style={{ display: 'flex', alignItems: 'center' }}>
                  {img}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          BOTTOM DARK BAR
      ══════════════════════════════════════════════════════ */}
      {showLegalBar && <div style={{ backgroundColor: colors.navyDeep }}>
        <div style={{
          maxWidth: '144rem', margin: '0 auto',
          padding: `1.8rem ${pad}`,
          display: 'flex', flexWrap: 'wrap',
          alignItems: 'center', justifyContent: 'space-between',
          gap: '1.2rem',
        }}>
          <div style={{ display: 'flex', gap: '2.4rem', flexWrap: 'wrap' }}>
            {legalLinks.map((link) => (
              <Link key={link.label} href={link.href} style={{
                fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
                color: 'rgba(255,255,255,0.5)', textDecoration: 'none',
              }}>{link.label}</Link>
            ))}
          </div>
          {copyrightText && (
            <p style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300, color: 'rgba(255,255,255,0.35)' }}
              dangerouslySetInnerHTML={{ __html: copyrightText }}
            />
          )}
        </div>
      </div>}

    </footer>
  );
}
